import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faCircleCheck, faClock, faPlus, faTicket } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useResellerCoupons, ResellerCoupon } from "../../../hooks/Reseller/useResellerCoupons"
import { EPlanCouponApprovalStatus } from "../../../Enum/EPlanCouponApprovalStatus"
import { EPlanCouponDiscountType } from "../../../Enum/EPlanCouponDiscountType"
import { convertToLocalMoney, formatDateToString } from "../../../Objects/Generics/Helps"
import { useResellerLogged } from "../../../context/useResellerContext"
import { RequestResellerCouponPopup } from "../RequestResellerCouponPopup"
import { FinancialDataTable } from "../../CashFlow/components/FinancialDataTable"
import { FinancialKpiCard } from "../../CashFlow/components/FinancialKpiCard"
import type { FinancialTableColumn } from "../../CashFlow/types/financialUi"

const statusStyle: Record<EPlanCouponApprovalStatus, string> = {
  [EPlanCouponApprovalStatus.Approved]: "bg-emerald-50 text-emerald-700",
  [EPlanCouponApprovalStatus.Pending]: "bg-amber-50 text-amber-700",
  [EPlanCouponApprovalStatus.Rejected]: "bg-red-50 text-red-700",
}

export const ResellerCouponsSection: React.FC = () => {
  const { t } = useTranslation("reseller")

  const { resellerProfile } = useResellerLogged()

  const { handleGetResellerCoupons } = useResellerCoupons()

  const [coupons, setCoupons] = useState<ResellerCoupon[] | null>(null)

  const [isRequestOpen, setIsRequestOpen] = useState(false)

  const refresh = () => handleGetResellerCoupons(setCoupons)

  useEffect(() => {
    refresh()
  }, [])

  const statusLabel = useCallback(
    (status: EPlanCouponApprovalStatus) => {
      if (status === EPlanCouponApprovalStatus.Approved) return t("coupons.status_approved")
      if (status === EPlanCouponApprovalStatus.Pending) return t("coupons.status_pending")
      return t("coupons.status_rejected")
    },
    [t],
  )

  const rows = coupons ?? []

  const formatDiscount = useCallback(
    (coupon: ResellerCoupon) =>
      coupon.discountType === EPlanCouponDiscountType.Percentage
        ? `${coupon.discountValue}%`
        : convertToLocalMoney(coupon.discountValue),
    [],
  )

  const columns = useMemo<FinancialTableColumn<ResellerCoupon>[]>(
    () => [
      {
        id: "code",
        label: t("coupons.column_code"),
        render: (coupon) => <span className="font-extrabold text-slate-800">{coupon.code}</span>,
        sortValue: (coupon) => coupon.code,
        mobilePriority: "primary",
      },
      {
        id: "discount",
        label: t("coupons.column_discount"),
        render: formatDiscount,
        sortValue: (coupon) => coupon.discountValue,
        align: "right",
        mobilePriority: "summary",
      },
      {
        id: "requestedAt",
        label: t("coupons.column_requested_at"),
        render: (coupon) => formatDateToString(coupon.createdAt),
        sortValue: (coupon) => new Date(coupon.createdAt).getTime(),
      },
      {
        id: "status",
        label: t("coupons.column_status"),
        render: (coupon) => (
          <div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusStyle[coupon.approvalStatus]}`}>
              {statusLabel(coupon.approvalStatus)}
            </span>

            {coupon.approvalStatus === EPlanCouponApprovalStatus.Rejected && coupon.rejectionReason && (
              <p className="mt-1 text-xs text-gray-400">{coupon.rejectionReason}</p>
            )}
          </div>
        ),
        sortValue: (coupon) => coupon.approvalStatus,
        mobilePriority: "summary",
      },
    ],
    [formatDiscount, statusLabel, t],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-3">
          <FinancialKpiCard title={t("coupons.title")} value={`${rows.length}`} icon={faTicket} tone="brand" />

          <FinancialKpiCard
            title={t("coupons.status_approved")}
            value={`${rows.filter((coupon) => coupon.approvalStatus === EPlanCouponApprovalStatus.Approved).length}`}
            icon={faCircleCheck}
            tone="positive"
          />

          <FinancialKpiCard
            title={t("coupons.status_pending")}
            value={`${rows.filter((coupon) => coupon.approvalStatus === EPlanCouponApprovalStatus.Pending).length}`}
            icon={faClock}
            tone="warning"
          />
        </div>

        <button
          type="button"
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:scale-[1.02] hover:shadow-md motion-reduce:transform-none motion-reduce:transition-none"
          onClick={() => setIsRequestOpen(true)}
          data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.requestCoupon"
        >
          <FontAwesomeIcon icon={faPlus} className="text-xs" />
          {t("coupons.request_title")}
        </button>
      </div>

      <FinancialDataTable
        title={t("coupons.title")}
        rows={rows}
        getRowKey={(coupon) => coupon.id}
        columns={columns}
        searchText={(coupon) => `${coupon.code} ${statusLabel(coupon.approvalStatus)}`}
        searchLabel={t("coupons.search")}
        emptyTitle={t("coupons.empty")}
        icon={faTicket}
        paginationLabels={{
          ariaLabel: t("coupons.title"),
          previous: t("table.previous"),
          next: t("table.next"),
          page: (current, total) => t("table.page", { current, total }),
          range: (first, last, total) => t("table.range", { first, last, total }),
          itemsPerPage: t("table.items_per_page"),
        }}
      />

      {isRequestOpen && (
        <RequestResellerCouponPopup
          suggestedCode={`${resellerProfile?.referralCode ?? ""}10`}
          onClose={() => setIsRequestOpen(false)}
          onSuccess={refresh}
        />
      )}
    </div>
  )
}
